import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, Row, Col } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { RejaPendientes, armaBloque, cambiaCeldaBloques, llenaBloques, lineasCapturadas } from "./RejaPendientes";
import { hoyLocal } from "./fechaLocal";

// Recibe el embarque sin salir del listado de embarques: trae lo pendiente por
// color y talla, se captura lo que llego y de un solo golpe se crea la entrada,
// se descuenta del embarque y entra al inventario (el back lo hace en una
// transaccion, antes eran cuatro peticiones por renglon desde el front).
const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const num = (n) => new Intl.NumberFormat("en-US").format(Number(n) || 0);


function RecibirEmbarqueModal({ abierto, cerrar, embarque, user, onRecibido }) {
  const [bloques, setBloques] = useState([]);
  const [areas, setAreas] = useState([]);
  const [area, setArea] = useState("");
  const [fecha, setFecha] = useState(hoyLocal());
  const [factura, setFactura] = useState("NA");
  const [observaciones, setObservaciones] = useState("NA");
  const [tipoCambio, setTipoCambio] = useState(19);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!abierto || !embarque) return;
    setBloques([]); setFecha(hoyLocal()); setFactura("NA"); setObservaciones("NA"); setGuardando(false);
    axios.get(process.env.REACT_APP_URL_AREAS, { headers: headers() })
      .then((r) => {
        const activas = (r.data || []).filter((a) => a.is_active === "Si" && !/general/i.test(a.name));
        setAreas(activas);
        const cedis = activas.find((a) => /cedis/i.test(a.name));
        setArea((prev) => prev || (cedis ? cedis._id : activas[0]?._id || ""));
      })
      .catch(() => {});
    axios.get(`${process.env.REACT_APP_URL_ARTICULOS_EMBARQUES}/embarque/${embarque._id}`, { headers: headers() })
      .then((r) => {
        const lineas = (r.data || [])
          .filter((a) => Number(a.pendiente_recibir) > 0)
          .map((a) => ({
            _id: a._id,
            articulo: a.articulos?.[0],
            color: a.colores?.[0] || null,
            talla: a.tallas?.[0] || null,
            pendiente: Number(a.pendiente_recibir) || 0,
            precio: Number(a.precio) || Number(a.costo) || 0,
          }))
          .filter((l) => l.articulo);
        setBloques(lineas.length
          ? [armaBloque(String(embarque._id), `Contenedor ${embarque.contenedor || ""}`, `Embarque ${embarque.numero || embarque.idEmbarques || ""}`, lineas)]
          : []);
      })
      .catch((e) => Swal.fire({ icon: "error", title: "No se pudo traer el embarque", text: e.message }));
  }, [abierto, embarque]); // eslint-disable-line

  const capturadas = lineasCapturadas(bloques);
  const piezas = capturadas.reduce((s, c) => s + c.cantidad, 0);

  async function recibir() {
    if (!area) { Swal.fire({ icon: "warning", title: "Falta el almacén", text: "Elige a qué almacén entra." }); return; }
    if (!capturadas.length) { Swal.fire({ icon: "warning", title: "Sin cantidades", text: "Captura lo que llegó." }); return; }
    const ok = await Swal.fire({
      icon: "question", title: `Recibir ${num(piezas)} piezas`,
      html: `${capturadas.length} renglón(es) entran al inventario y se crea la entrada.`,
      showCancelButton: true, confirmButtonText: "Sí, recibir", cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    setGuardando(true);
    try {
      const r = await axios.post(`${process.env.REACT_APP_URL_COMPRAS}Recepcion`, {
        embarques: embarque._id, areas: area, fecha, factura, observaciones,
        tipoCambio: Number(tipoCambio) || 1, moneda: "USD", user,
        lineas: capturadas.map((c) => ({
          articulosEmbarques: c.linea._id,
          articulos: c.linea.articulo?._id,
          colores: c.linea.color?._id || null,
          tallas: c.linea.talla?._id || null,
          cantidad: c.cantidad,
          precio: c.linea.precio,
        })),
      }, { headers: headers() });
      setGuardando(false);
      await Swal.fire({
        icon: "success", title: `Entrada ${r.data.idCompras} creada`,
        html: `${num(r.data.total_cantidad)} piezas en ${r.data.renglones} renglón(es).` +
              (r.data.embarqueCompleto ? "<br>El embarque quedó <b>recibido completo</b>." : "<br>Todavía queda mercancía por recibir."),
      });
      cerrar();
      if (onRecibido) onRecibido();
    } catch (error) {
      setGuardando(false);
      Swal.fire({ icon: "error", title: "No se pudo recibir", text: error.response?.data?.message || error.message });
    }
  }

  return (
    <Modal size="xl" isOpen={abierto} toggle={cerrar}>
      <ModalHeader toggle={cerrar}>
        Recibir embarque {embarque?.numero || embarque?.idEmbarques} {embarque?.contenedor ? `· ${embarque.contenedor}` : ""}
      </ModalHeader>
      <ModalBody>
        <Row className="mb-3">
          <Col md={2}>
            <Label>Fecha</Label>
            <Input bsSize="sm" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </Col>
          <Col md={3}>
            <Label>Almacén</Label>
            <Input bsSize="sm" type="select" value={area} onChange={(e) => { const v = e.target.value; setArea(v); }}>
              <option value="">Selecciona</option>
              {areas.map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
            </Input>
          </Col>
          <Col md={2}>
            <Label>Factura</Label>
            <Input bsSize="sm" value={factura} onChange={(e) => { const v = e.target.value; setFactura(v); }} />
          </Col>
          <Col md={2}>
            <Label>Tipo de cambio</Label>
            <Input bsSize="sm" type="number" step="any" value={tipoCambio} onChange={(e) => { const v = e.target.value; setTipoCambio(v); }} />
          </Col>
          <Col md={3}>
            <Label>Observaciones</Label>
            <Input bsSize="sm" value={observaciones} onChange={(e) => { const v = e.target.value; setObservaciones(v); }} />
          </Col>
        </Row>
        {embarque?.recibido === "Si" && bloques.length ? (
          <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "6px 10px", marginBottom: 10, fontSize: 13 }}>
            Este embarque ya está marcado como <b>recibido</b>, pero todavía tiene mercancía pendiente. Al capturarla se descuenta normal.
          </div>
        ) : null}
        {!bloques.length ? (
          <p style={{ color: "#9ca3af" }}>Este embarque ya no tiene nada pendiente de recibir.</p>
        ) : null}
        <RejaPendientes
          bloques={bloques}
          etiquetaPendiente="Por recibir"
          onCambia={(b, a, c, t, v) => setBloques((prev) => cambiaCeldaBloques(prev, b, a, c, t, v))}
          onLlenar={(b, a, c) => setBloques((prev) => llenaBloques(prev, b, a, true, c))}
          onLimpiar={(b, a, c) => setBloques((prev) => llenaBloques(prev, b, a, false, c))}
          onQuitar={() => {}}
        />
      </ModalBody>
      <ModalFooter>
        <Label style={{ marginRight: "auto", color: "#64748b", fontSize: 12 }}>
          {capturadas.length} renglón(es) · <b>{num(piezas)}</b> piezas
        </Label>
        <Button color="success" onClick={recibir} disabled={guardando || !piezas}>
          {guardando ? "Recibiendo..." : "Recibir"}
        </Button>
        <Button color="secondary" onClick={cerrar}>Cerrar</Button>
      </ModalFooter>
    </Modal>
  );
}

export default RecibirEmbarqueModal;
